import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';

const availableOf = (v) => (v.stockQuantity ?? 0) - (v.reservedQuantity ?? 0);

/**
 * Moves a whole product to another store: for every variant, all the available stock (stock minus
 * reserved) is transferred; reserved units stay in the source store. The product, its attributes and
 * variants are created in the destination store if they don't exist there yet (categories are global).
 */
const ProductTransferDialog = ({ open, onClose, onSaved, product, variants = [] }) => {
  const { t } = useTranslation();
  const [destinations, setDestinations] = useState([]);
  const [destinationId, setDestinationId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDestinationId('');
    setReason('');
    productService
      .getTransferDestinations(product?.storeId)
      .then((list) => setDestinations(list || []))
      .catch(() => setDestinations([]));
  }, [open, product?.storeId]);

  const movable = variants.filter((v) => availableOf(v) > 0);
  const totalUnits = movable.reduce((sum, v) => sum + availableOf(v), 0);
  const canSave = movable.length > 0 && destinationId && reason.trim() && !saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await productService.transferProduct({
        productId: product.id,
        destinationStoreId: destinationId,
        reason: reason.trim(),
      });
      toast.success(
        t('stockMovements.transferProductDone', {
          units: result.totalUnits,
          store: result.destinationStoreName,
          count: result.variantsTransferred,
        })
      );
      if (result.variantsSkipped > 0) {
        toast.info(t('stockMovements.transferProductSkipped', { count: result.variantsSkipped }));
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('stockMovements.transferProductTitle')}
        <Typography variant="body2" color="text.secondary">
          {product?.name}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Alert severity="info">{t('stockMovements.transferProductHint')}</Alert>

        {movable.length === 0 ? (
          <Alert severity="warning">{t('stockMovements.transferProductNothing')}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('stockMovements.variant')}</TableCell>
                <TableCell align="right">{t('stockMovements.available')}</TableCell>
                <TableCell align="right">{t('stockMovements.toTransfer')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((v) => {
                const avail = availableOf(v);
                return (
                  <TableRow key={v.id} sx={avail <= 0 ? { opacity: 0.5 } : undefined}>
                    <TableCell>
                      {v.shortName || v.name} — {v.sku}
                    </TableCell>
                    <TableCell align="right">
                      {avail} ({v.reservedQuantity ?? 0} {t('stockMovements.reservedShort')})
                    </TableCell>
                    <TableCell align="right">{Math.max(avail, 0)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell colSpan={2}>
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalUnits}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        <FormControl fullWidth>
          <InputLabel>{t('stockMovements.destinationStore')}</InputLabel>
          <Select
            value={destinationId}
            label={t('stockMovements.destinationStore')}
            onChange={(e) => setDestinationId(e.target.value)}
          >
            {destinations.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name} ({d.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {destinationId && <Alert severity="info">{t('stockMovements.transferAutoCreateHint')}</Alert>}

        <TextField
          fullWidth
          multiline
          minRows={2}
          required
          label={t('stockMovements.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {t('stockMovements.transferProduct')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductTransferDialog;
